import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { MapPinIcon, PhoneIcon, MailIcon, LinkedInIcon, InstagramIcon } from '../components/Icons';
import { validateEmail, validateRequired } from '../validation';
import './Contact.css';

interface FormState {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const Contact: React.FC = () => {
  const [form, setForm]           = useState<FormState>({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors]       = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending]     = useState(false);
  const [sendError, setSendError] = useState('');

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!validateRequired(form.name))    next.name    = 'Name is required.';
    if (!validateRequired(form.email))   next.email   = 'Email is required.';
    else if (!validateEmail(form.email)) next.email   = 'Enter a valid email address.';
    if (!validateRequired(form.phone))   next.phone   = 'Phone number is required.';
    if (!validateRequired(form.message)) next.message = 'Message is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormState]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    setSendError('');
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        {
          from_name:  form.name,
          from_email: form.email,
          phone:      form.phone,
          message:    form.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!
      );
      setSubmitted(true);
    } catch {
      setSendError('Failed to send your message. Please try again or email us directly at drofflogistics@gmail.com.');
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setForm({ name: '', email: '', phone: '', message: '' });
    setErrors({});
    setSendError('');
    setSubmitted(false);
  };

  return (
    <main className="contact-page">
      {/* PAGE HEADER */}
      <section className="contact-header">
        <img className="contact-header__bg" src="/contact-hero.jpg" alt="" aria-hidden="true" />
        <div className="contact-header__overlay" />
        <div className="container contact-header__body">
          <p className="section-label">Connect with Droff</p>
          <h1 className="contact-header__title">Get in Touch</h1>
          <p className="contact-header__desc">
            Our dedicated team is ready to assist with your logistical needs. Whether it's a quote or a quick tracking update, we're here to keep your business moving.
          </p>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="contact-body">
        <div className="container contact-body__grid">
          {/* FORM */}
          <div className="contact-form-wrap">
            <h2 className="contact-form-wrap__heading">Send us a Message</h2>

            {submitted ? (
              <div className="contact-success">
                <p>Thanks! We'll be in touch within 24 hours.</p>
                <button className="btn-primary" onClick={handleReset}>Send Another</button>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="contact-form__row">
                  <div className="contact-form__field">
                    <label htmlFor="name">Name</label>
                    <input
                      id="name" name="name" type="text" placeholder="John Doe"
                      value={form.name} onChange={handleChange}
                      className={errors.name ? 'input-error' : ''}
                      aria-describedby={errors.name ? 'err-name' : undefined}
                    />
                    {errors.name && <span id="err-name" className="field-error" role="alert">{errors.name}</span>}
                  </div>
                  <div className="contact-form__field">
                    <label htmlFor="email">Email</label>
                    <input
                      id="email" name="email" type="email" placeholder="john@example.com"
                      value={form.email} onChange={handleChange}
                      className={errors.email ? 'input-error' : ''}
                      aria-describedby={errors.email ? 'err-email' : undefined}
                    />
                    {errors.email && <span id="err-email" className="field-error" role="alert">{errors.email}</span>}
                  </div>
                </div>

                <div className="contact-form__field">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    id="phone" name="phone" type="tel" placeholder="+233 XX XXX XXXX"
                    value={form.phone} onChange={handleChange}
                    className={errors.phone ? 'input-error' : ''}
                    aria-describedby={errors.phone ? 'err-phone' : undefined}
                  />
                  {errors.phone && <span id="err-phone" className="field-error" role="alert">{errors.phone}</span>}
                </div>

                <div className="contact-form__field">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message" name="message" rows={5} placeholder="How can we help you today?"
                    value={form.message} onChange={handleChange}
                    className={errors.message ? 'input-error' : ''}
                    aria-describedby={errors.message ? 'err-message' : undefined}
                  />
                  {errors.message && <span id="err-message" className="field-error" role="alert">{errors.message}</span>}
                </div>

                {sendError && <p className="send-error" role="alert">{sendError}</p>}

                <button
                  type="submit"
                  className="btn-primary contact-form__submit"
                  disabled={sending}
                >
                  {sending ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

          {/* INFO */}
          <div className="contact-info">
            <div className="contact-info__item">
              <div className="contact-info__icon"><MapPinIcon size={18} strokeWidth={1.8} /></div>
              <div>
                <p className="contact-info__label">Office Address</p>
                <p className="contact-info__val">No. 5 Numo Tetteh Kwao St, Adjiriganor,<br />East Legon, Accra, Ghana</p>
              </div>
            </div>
            <div className="contact-info__item">
              <div className="contact-info__icon"><PhoneIcon size={18} strokeWidth={1.8} /></div>
              <div>
                <p className="contact-info__label">Phone Line</p>
                <p className="contact-info__val">+233(0) 55 994 1115</p>
                <p className="contact-info__sub">Available 24/7 for emergency logistics.</p>
              </div>
            </div>
            <div className="contact-info__item">
              <div className="contact-info__icon"><MailIcon size={18} strokeWidth={1.8} /></div>
              <div>
                <p className="contact-info__label">Email Support</p>
                <p className="contact-info__val">drofflogistics@gmail.com</p>
                <p className="contact-info__sub">Typical response time: &lt; 2 hours.</p>
              </div>
            </div>
            <div className="contact-info__socials">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="contact-info__social-btn">
                <LinkedInIcon size={16} strokeWidth={1.8} /> LinkedIn
              </a>
              <a href="https://www.instagram.com/drofflogistics?igsh=MXc4OWZ5MTJvMXJ5dQ==" target="_blank" rel="noopener noreferrer" className="contact-info__social-btn">
                <InstagramIcon size={16} strokeWidth={1.8} /> Instagram
              </a>
            </div>
          </div>
        </div>

        {/* MAP */}
        <div className="container">
          <div className="contact-map">
            <iframe
              title="Droff Logistics — East Legon, Accra"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-0.1800%2C5.6000%2C-0.1400%2C5.6300&layer=mapnik&marker=5.6150%2C-0.1600"
              width="100%" height="340" style={{ border: 0 }} loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--red)', padding: '72px 0' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontSize: 'clamp(22px,3vw,34px)', fontWeight: 700, color: '#fff', maxWidth: 480, marginBottom: 8 }}>
              Ready to ship your cargo?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15 }}>Get an instant quote for your logistics needs today.</p>
          </div>
          <Link to="/contact" className="btn-primary" style={{ background: '#fff', color: 'var(--red)', borderColor: '#fff' }}>
            Request a Quote
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Contact;
